// utils/useOperatorActions.js
import { useEffect, useMemo, useRef } from "react";
import { useOperator } from "./OperatorContext";

/** Decide if an event should be handled by this runtime (tvId or wall). */
function tvMatches(evtTvId, runtimeTvId, acceptGlobalOnTv = false) {
  const e = evtTvId == null ? null : String(evtTvId);
  const r = runtimeTvId == null ? null : String(runtimeTvId);

  // If you want TVs to ALSO react to global events, set acceptGlobalOnTv = true
  if (e === null && r !== null) {
    return acceptGlobalOnTv; // false by default (TVs ignore global)
  }

  // TV-scoped event going to the global wall? ignore
  if (e !== null && r === null) return false;

  // Both have tvIds — must match
  if (e !== null && r !== null) return e === r;

  // Both null => global to wall
  return true;
}

/** Get the runtime tvId: URL (?id=...), then OperatorProvider (preview), else null (wall). */
function useRuntimeTvId() {
  const urlTvId = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const raw = sp.get("id");
      return raw != null ? String(raw) : null;
    } catch {
      return null;
    }
  }, []);

  let operatorTvId = null;
  try {
    const ctx = useOperator(); // undefined if no provider
    operatorTvId = ctx?.tvId != null ? String(ctx.tvId) : null;
  } catch {
    operatorTvId = null;
  }

  return urlTvId ?? operatorTvId ?? null;
}

/**
 * Hybrid hook:
 * - Reacts to OperatorContext.activeActions (cross-tab)
 * - Reacts to window CustomEvents (same-tab)
 * - Filters by tvId so Days are reusable on wall & TVs.
 */
export function useOperatorActions(actionIds, callback, options = {}) {
  const ids = Array.isArray(actionIds) ? actionIds : [actionIds];
  const { activeActions } = useOperator() || {};
  const runtimeTvId = useRuntimeTvId();
  const savedCb = useRef(callback);
  savedCb.current = callback;

  const { acceptGlobalOnTv = false } = options;

  // 1) Same-tab events (operator preview / TV replays inside same tab)
  useEffect(() => {
    const onEvent = (e) => {
      const evtId = e?.type;
      if (!ids.includes(evtId)) return;

      const detail = e?.detail ?? {};
      const evtTvId = detail?.tvId ?? null;

      if (!tvMatches(evtTvId, runtimeTvId, acceptGlobalOnTv)) return;

      // Fire callback with a consistent shape
      savedCb.current({
        id: evtId,
        payload: detail,
        ts: Date.now(), // synthetic ts (window events don't carry ts)
      });
    };

    ids.forEach((id) => window.addEventListener(id, onEvent));
    return () => ids.forEach((id) => window.removeEventListener(id, onEvent));
  }, [ids, runtimeTvId, acceptGlobalOnTv]);

  // 2) Cross-tab updates via OperatorContext.activeActions (localStorage -> storage)
  const lastTsRef = useRef(0);
  useEffect(() => {
    if (!activeActions?.length) return;

    const latest = activeActions[activeActions.length - 1];
    if (!latest || !ids.includes(latest.id)) return;

    const evtTvId = latest?.payload?.tvId ?? null;
    if (!tvMatches(evtTvId, runtimeTvId, acceptGlobalOnTv)) return;

    // De-dupe by ts (your original behavior)
    if (latest.ts === lastTsRef.current) return;
    lastTsRef.current = latest.ts;

    savedCb.current(latest);
  }, [activeActions, ids, runtimeTvId, acceptGlobalOnTv]);
}
